import {
  Badge,
  Box,
  Checkbox,
  HStack,
  IconButton,
  Switch,
  Table,
  Text,
  Tooltip,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdDelete, MdRefresh } from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import { getThemeColor } from "theme/colors";
import { KbDocumentDto, KbDocumentStatus } from "types/knowledgeBase";
import DocumentStatusBadge from "./DocumentStatusBadge";
import MoveFolderPopover from "./MoveFolderPopover";

interface DocumentsTableProps {
  documents: KbDocumentDto[];
  onDelete: (docId: string) => void;
  onRefreshStatus: (docId: string) => void;
  onDocumentUpdated?: (doc: KbDocumentDto) => void;
  isDeleting?: string;
  isRefreshing?: string;
  /** Optional — if provided, shows "Move to folder" button on each row */
  onMove?: (id: string, folderId: string | null) => Promise<void>;
  folders?: Array<{ id: string; name: string; parentName?: string }>;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleAll?: (checked: boolean) => void;
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentsTable = ({
  documents,
  onDelete,
  onRefreshStatus,
  onDocumentUpdated,
  isDeleting,
  isRefreshing,
  onMove,
  folders,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}: DocumentsTableProps) => {
  const { t } = useTranslation();
  const [togglingGlobal, setTogglingGlobal] = useState<string | null>(null);

  const handleToggleGlobal = async (doc: KbDocumentDto) => {
    setTogglingGlobal(doc.id);
    try {
      const updated = await knowledgeBaseService.setDocumentGlobal(
        doc.id,
        !doc.isGlobal,
      );
      onDocumentUpdated?.(updated);
    } catch (err) {
      console.error("Failed to toggle global flag", err);
    } finally {
      setTogglingGlobal(null);
    }
  };

  return (
    <Box overflowX="auto">
      <Table.Root minW="600px">
        <Table.Header>
          <Table.Row>
            {onToggleAll && selectedIds && (
              <Table.ColumnHeader w="40px">
                <Checkbox
                  size="sm"
                  checked={
                    documents.length > 0 &&
                    selectedIds.size === documents.length
                      ? true
                      : selectedIds.size > 0
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(e: {
                    checked: boolean | "indeterminate";
                  }) => onToggleAll(e.checked === true)}
                />
              </Table.ColumnHeader>
            )}
            <Table.ColumnHeader>
              {t("app.knowledgeBase.table.fileName")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.table.size")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.table.status")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>Global</Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.table.uploadedAt")}
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">
              {t("app.common.actions")}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {documents.map((doc) => (
            <Table.Row
              key={doc.id}
              draggable={!!onMove}
              onDragStart={(e) => {
                e.dataTransfer.setData("itemType", "document");
                e.dataTransfer.setData("itemId", doc.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              cursor={onMove ? "grab" : undefined}
              _active={onMove ? { cursor: "grabbing" } : {}}
              bg={
                selectedIds?.has(doc.id)
                  ? { base: "blue.50", _dark: "blue.900" }
                  : undefined
              }
            >
              {onToggleSelect && selectedIds && (
                <Table.Cell w="40px" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size="sm"
                    checked={selectedIds.has(doc.id)}
                    onCheckedChange={() => onToggleSelect(doc.id)}
                  />
                </Table.Cell>
              )}
              <Table.Cell>
                <Box>
                  <HStack gap={1}>
                    <Text fontWeight="medium" truncate maxW="260px">
                      {doc.fileName}
                    </Text>
                    {doc.folderName && (
                      <Badge size="sm" variant="subtle" colorPalette="gray">
                        {doc.folderName}
                      </Badge>
                    )}
                  </HStack>
                  {doc.errorMessage && (
                    <Tooltip content={doc.errorMessage}>
                      <Text fontSize="xs" color="red.500" truncate maxW="300px">
                        {doc.errorMessage}
                      </Text>
                    </Tooltip>
                  )}
                </Box>
              </Table.Cell>
              <Table.Cell>
                <Text color={getThemeColor("text.secondary")}>
                  {formatFileSize(doc.fileSizeBytes)}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <DocumentStatusBadge status={doc.status} />
              </Table.Cell>
              <Table.Cell>
                <Tooltip
                  content={
                    doc.isGlobal ? "Included in all widgets" : "Make global"
                  }
                >
                  <Switch
                    size="sm"
                    colorPalette="purple"
                    checked={doc.isGlobal}
                    onCheckedChange={() => handleToggleGlobal(doc)}
                    disabled={togglingGlobal === doc.id}
                  />
                </Tooltip>
              </Table.Cell>
              <Table.Cell>
                <Text color={getThemeColor("text.secondary")}>
                  {new Date(doc.createdAt).toLocaleString()}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <HStack gap={1} justify="flex-end">
                  {onMove && folders && (
                    <MoveFolderPopover
                      folders={folders}
                      currentFolderId={doc.folderId}
                      onMove={(fid) => onMove(doc.id, fid)}
                    />
                  )}
                  {(doc.status === KbDocumentStatus.Processing ||
                    doc.status === KbDocumentStatus.Uploaded) && (
                    <Tooltip content={t("app.knowledgeBase.refreshStatus")}>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t("app.knowledgeBase.refreshStatus")}
                        onClick={() => onRefreshStatus(doc.id)}
                        disabled={isRefreshing === doc.id}
                        loading={isRefreshing === doc.id}
                      >
                        <MdRefresh />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip content={t("app.common.delete")}>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      colorPalette="red"
                      aria-label={t("app.common.delete")}
                      onClick={() => onDelete(doc.id)}
                      disabled={isDeleting === doc.id}
                    >
                      <MdDelete />
                    </IconButton>
                  </Tooltip>
                </HStack>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export default DocumentsTable;
